import { z } from 'zod';

/** Taxonomías controladas (schemas/taxonomies.json, spec §7.3). */
export const PLACE_TYPE_LABEL: Record<string, string> = {
  church: 'Iglesia', monastery: 'Monasterio', convent: 'Convento', cathedral: 'Catedral', palace: 'Palacio',
  fortification: 'Fortificación', gate: 'Puerta', infrastructure: 'Infraestructura', 'urban-space': 'Espacio urbano',
  'civil-building': 'Edificio civil', landscape: 'Paisaje', settlement: 'Localidad', other: 'Otro',
};
export const STATUS_LABEL: Record<string, string> = {
  preserved: 'Conservado', transformed: 'Transformado', lost: 'Desaparecido', undetermined: 'No determinado',
};
export const PRECISION_LABEL: Record<string, string> = {
  exact: 'Exacta', building: 'Parcela / edificio', vicinity: 'Entorno', municipality: 'Término municipal',
  approximate: 'Aproximada', unknown: 'Desconocida',
};
export const CONFIDENCE_LABEL: Record<string, string> = {
  confirmed: 'Confirmada', probable: 'Probable', uncertain: 'Incierta', rejected: 'Rechazada',
};

export const ParagraphSchema = z.object({
  id: z.string(),
  chapterId: z.string(),
  sequence: z.number(),
  kind: z.enum(['body', 'heading', 'footnote', 'caption', 'epigraph']),
  text: z.string(),
  printedPageStart: z.number().optional(),
  printedPageEnd: z.number().optional(),
  leafStart: z.number(),
  leafEnd: z.number(),
  pageBreakOffset: z.number().optional(),
  /** todos los saltos de página del párrafo, en orden: los hay que cruzan dos y tres páginas */
  pageBreaks: z.array(z.object({
    offset: z.number(),
    leaf: z.number(),
    printedPage: z.number().nullable().optional(),
  })).optional(),
  footnoteIds: z.array(z.string()).optional(),
  editorialNotes: z.array(z.string()).optional(),
  correctionCount: z.number().optional(),
  reviewStatus: z.enum(['draft', 'reviewed', 'published']),
});
export type Paragraph = z.infer<typeof ParagraphSchema>;

export const ChapterSchema = z.object({ chapterId: z.string(), title: z.string(), paragraphs: z.array(ParagraphSchema) });
export type Chapter = z.infer<typeof ChapterSchema>;

export const ChapterIndexSchema = z.object({
  workId: z.string(),
  chapters: z.array(z.object({
    chapterId: z.string(), number: z.string(), title: z.string(),
    collectionId: z.string(), province: z.string(), city: z.string().nullable(), level: z.string(), part: z.string(),
    printedPageStart: z.number(), printedPageEnd: z.number(),
    paragraphs: z.number(), footnotes: z.number(), words: z.number(),
  })),
});
export type ChapterIndex = z.infer<typeof ChapterIndexSchema>;

export const MentionSchema = z.object({
  id: z.string(),
  placeId: z.string(),
  paragraphId: z.string(),
  surfaceForm: z.string(),
  charStart: z.number(),
  charEnd: z.number(),
  printedPage: z.number().optional(),
  leaf: z.number().optional(),
  contextBefore: z.string().optional(),
  contextAfter: z.string().optional(),
  textConfidence: z.enum(['confirmed', 'probable', 'uncertain', 'rejected']),
  reviewStatus: z.enum(['draft', 'reviewed', 'published']),
  note: z.string().optional(),
});
export type Mention = z.infer<typeof MentionSchema>;

const JcylRecordSchema = z.object({
  externalId: z.string(), nombre: z.string(), tipoMonumento: z.string(), clasificacion: z.string(),
  periodoHistorico: z.string(), calle: z.string(), bicIds: z.array(z.string()), descripcion_texto: z.string(),
  url: z.string(), retrievedAt: z.string(), sourceId: z.string(),
});
export type JcylRecord = z.infer<typeof JcylRecordSchema>;

const StatusAssertionSchema = z.object({
  id: z.string(), value: z.string(), sourceId: z.string(), sourceLocator: z.string().nullable().optional(),
  attributionType: z.string(), confidence: z.string(), note: z.string().nullable().optional(),
});
export type StatusAssertion = z.infer<typeof StatusAssertionSchema>;

/** Identificadores de fuente del source_manifest y su tipo de procedencia en la interfaz. */
export const SOURCE_KIND: Record<string, 'quadrado' | 'jcyl' | 'osm' | 'wikidata' | 'editorial'> = {
  ia_quadrado_sav_1884: 'quadrado', jcyl_relacion_monumentos: 'jcyl', osm_overpass_2026_09_17: 'osm', wikidata: 'wikidata', editorial_1884: 'editorial',
};
export const ATTRIBUTION_LABEL: Record<string, string> = {
  quoted: 'cita literal', reported: 'dato de la fuente', 'editorial-inference': 'inferencia editorial',
};

export const PlaceFeatureSchema = z.object({
  type: z.literal('Feature'),
  id: z.string(),
  geometry: z.object({ type: z.literal('Point'), coordinates: z.tuple([z.number(), z.number()]) }).nullable(),
  properties: z.object({
    id: z.string(), slug: z.string(), canonicalName: z.string(), quadradoNames: z.array(z.string()),
    type: z.string(), currentStatus: z.string(), identityConfidence: z.string(), reviewStatus: z.string(),
    collectionId: z.string(), municipality: z.string().optional(), province: z.string().optional(),
    externalRecords: z.array(z.object({ sourceId: z.string(), externalId: z.string(), url: z.string().optional(), retrievedAt: z.string(), matchScore: z.number().optional(), matchNote: z.string().optional() })),
    editorialSummary: z.string().optional(), geometryPrecision: z.string(), mentionCount: z.number(),
    reviewedBy: z.array(z.string()).optional(), lastReviewed: z.string().nullable().optional(),
    jcyl: z.array(JcylRecordSchema).default([]),
    geometrySource: z.string().nullable().optional(),
    geometryNote: z.string().nullable().optional(),
    statusAssertions: z.array(StatusAssertionSchema).default([]),
  }),
});
export type PlaceFeature = z.infer<typeof PlaceFeatureSchema>;
export const PlacesSchema = z.object({ type: z.literal('FeatureCollection'), attribution: z.string(), features: z.array(PlaceFeatureSchema) });

export const FacsimileIndexSchema = z.object({
  attribution: z.string(),
  pages: z.array(z.object({
    // 'volume' son las hojas que no pertenecen a ningún capítulo (cubiertas, portada, índice
    // general, plantilla de láminas) y 'blank', las guardas y los blancos entre láminas (fase F)
    leaf: z.number(), printedPage: z.number().nullable(), kind: z.enum(['text', 'plate', 'volume', 'blank']),
    caption: z.string().nullable(), file: z.string(), width: z.number(), height: z.number(),
  })),
});
export type FacsimileIndex = z.infer<typeof FacsimileIndexSchema>;
export type FacsimilePage = FacsimileIndex['pages'][number];

export const ItineraryStopSchema = z.object({
  order: z.number(), placeId: z.string(), placeName: z.string(), placeSlug: z.string(),
  currentStatus: z.string(), geometryPrecision: z.string(), located: z.boolean(),
  paragraphId: z.string(), mentionId: z.string(), printedPage: z.number().nullable().optional(),
  quote: z.string(), editorialNote: z.string(), question: z.string(),
});
export type ItineraryStop = z.infer<typeof ItineraryStopSchema>;

export const ItinerarySchema = z.object({
  id: z.string(), slug: z.string(), number: z.number(), collectionIds: z.array(z.string()), title: z.string(), subtitle: z.string().optional(),
  summary: z.string(), objectives: z.array(z.string()), durationMinutes: z.number(), level: z.string(),
  accessibility: z.string(), closing: z.string(), stops: z.array(ItineraryStopSchema),
  stopCount: z.number(), locatedStopCount: z.number(), straightLineMeters: z.number(), distanceNote: z.string(),
  reviewStatus: z.string(), reviewedBy: z.array(z.string()).optional(), lastReviewed: z.string().optional(),
  license: z.string().optional(),
});
export type Itinerary = z.infer<typeof ItinerarySchema>;
export const ItinerariesSchema = z.object({ itineraries: z.array(ItinerarySchema) });

const PassageSchema = z.object({
  placeId: z.string(), placeName: z.string(), placeSlug: z.string(), paragraphId: z.string(),
  mentionId: z.string(), printedPage: z.number().nullable().optional(), quote: z.string(),
  position: z.number().optional(), key: z.string().optional(),
});
export type Passage = z.infer<typeof PassageSchema>;

export const ActivityItemSchema = z.object({
  n: z.number(), prompt: z.string(),
  passage: PassageSchema.optional(),
  options: z.array(z.object({ placeId: z.string().optional(), placeName: z.string().optional(), placeSlug: z.string().optional(), key: z.string().optional(), quote: z.string().optional(), paragraphId: z.string().optional(), mentionId: z.string().optional(), printedPage: z.number().nullable().optional() })).optional(),
  entries: z.array(PassageSchema).optional(),
  orderBy: z.enum(['route', 'book']).optional(),
  itineraryId: z.string().nullable().optional(),
  answerPlaceId: z.string().optional(),
  answerKey: z.string().optional(),
  answerStatus: z.string().optional(),
  status: z.object({
    value: z.string(), sourceId: z.string().nullable().optional(), sourceLabel: z.string().nullable().optional(),
    sourceLocator: z.string().nullable().optional(), attributionType: z.string().nullable().optional(),
    confidence: z.string(), note: z.string().nullable().optional(),
  }).optional(),
  hint: z.string().nullable().optional(),
  explanation: z.string().nullable().optional(),
  evidence: z.object({ mentionId: z.string(), paragraphId: z.string(), printedPage: z.number().nullable().optional() }).optional(),
});
export type ActivityItem = z.infer<typeof ActivityItemSchema>;

export const ActivitySchema = z.object({
  id: z.string(), slug: z.string(), number: z.number(), collectionIds: z.array(z.string()), mechanic: z.enum(['detective', 'order', 'evidence', 'status']),
  title: z.string(), subtitle: z.string().optional(), summary: z.string(), instructions: z.string(),
  objectives: z.array(z.string()), estimatedMinutes: z.number(), level: z.string(), closing: z.string(),
  items: z.array(ActivityItemSchema), itemCount: z.number().optional(),
  reviewStatus: z.string(), reviewedBy: z.array(z.string()).optional(), lastReviewed: z.string().optional(),
  license: z.string().optional(),
});
export type Activity = z.infer<typeof ActivitySchema>;
export const ActivitiesSchema = z.object({ activities: z.array(ActivitySchema) });
export const MECHANIC_LABEL: Record<string, string> = {
  detective: 'Detective de lugares', order: 'Reconstruye el itinerario',
  evidence: 'Encuentra la evidencia', status: '¿Sigue existiendo?',
};

export const TeachingGuideSchema = z.object({
  id: z.string(), title: z.string(), subtitle: z.string().optional(), version: z.string().optional(),
  audience: z.string().optional(), intro: z.string(), quickGuide: z.array(z.string()),
  sourceLiteracy: z.string().optional(), statusExplained: z.string().optional(),
  beforeYouStart: z.array(z.string()).default([]), noMapAlternatives: z.array(z.string()).default([]),
  accessibility: z.array(z.string()).default([]), ethics: z.string().optional(), assessment: z.string().optional(),
  rubrics: z.array(z.object({ criterion: z.string(), levels: z.array(z.string()) })),
  project: z.object({ title: z.string(), sessions: z.string(), summary: z.string(), steps: z.array(z.string()), deliverable: z.string() }).optional(),
  glossary: z.array(z.object({ term: z.string(), definition: z.string() })),
  rights: z.string(), privacy: z.string(),
  reviewedBy: z.array(z.string()).optional(), lastReviewed: z.string().optional(),
});
export type TeachingGuide = z.infer<typeof TeachingGuideSchema>;

export const SequenceSchema = z.object({
  id: z.string(), slug: z.string(), number: z.number(), collectionIds: z.array(z.string()), title: z.string(), stage: z.string(), duration: z.string(),
  subjects: z.array(z.string()), objectives: z.array(z.string()), competences: z.array(z.string()).default([]),
  priorKnowledge: z.string().optional(), materials: z.array(z.string()).default([]), grouping: z.string().optional(),
  preparation: z.string().optional(),
  resources: z.array(z.object({ kind: z.enum(['itinerary', 'activity']), id: z.string(), slug: z.string(), title: z.string() })),
  development: z.array(z.object({ minutes: z.string(), what: z.string(), how: z.string() })),
  expectedEvidence: z.array(z.string()), diversity: z.array(z.string()).default([]),
  studentSheet: z.array(z.string()), assessment: z.string(), extension: z.string().optional(), closing: z.string().optional(),
});
export type Sequence = z.infer<typeof SequenceSchema>;

export const SolutionsSchema = z.array(z.object({
  activityId: z.string(), collectionIds: z.array(z.string()), title: z.string(), mechanic: z.string(),
  items: z.array(z.object({ n: z.number(), question: z.string(), answer: z.string(), reasoning: z.string().nullable().optional(), reference: z.string() })),
}));
export type Solutions = z.infer<typeof SolutionsSchema>;
export const TeachingSchema = z.object({ guide: TeachingGuideSchema, sequences: z.array(SequenceSchema), solutions: SolutionsSchema });
export type Teaching = z.infer<typeof TeachingSchema>;

/** Índice global de lugares (`places-index.json`): lo mínimo para buscar en todo el libro. */
export const PlaceIndexSchema = z.object({ places: z.array(z.object({
  id: z.string(), n: z.string(), q: z.array(z.string()).default([]), c: z.string(),
  t: z.string(), m: z.string().nullable().optional(), s: z.string(),
})) });
export type PlaceIndexEntry = z.infer<typeof PlaceIndexSchema>['places'][number];

/** Índice de búsqueda de una colección: el texto normalizado de cada párrafo. */
export const SearchIndexSchema = z.object({ collectionId: z.string(), paragraphs: z.array(z.object({
  i: z.string(), c: z.string(), k: z.string(), p: z.number().nullable().optional(), t: z.string(),
})) });
export type SearchIndex = z.infer<typeof SearchIndexSchema>;
export type SearchParagraph = SearchIndex['paragraphs'][number];

/** Cifras de la portada (`stats.json`), contadas por el ETL: nada se escribe a mano. */
export const StatsSchema = z.object({
  generated: z.string(), provinces: z.number(), collections: z.number(), chapters: z.number(),
  words: z.number(), places: z.number(), placesWithRecord: z.number(), placesLocated: z.number(),
  placesUndetermined: z.number(), municipalities: z.number(), mentions: z.number(),
  itineraries: z.number(), activities: z.number(), sequences: z.number(), facsimileLeaves: z.number(),
  datasets: z.array(z.object({
    id: z.string(), kind: z.string(), title: z.string(), publisher: z.string(),
    license: z.string(), url: z.string(), records: z.number().nullable().optional(), role: z.string(),
  })),
});
export type Stats = z.infer<typeof StatsSchema>;

/** Silueta de Castilla y León y de las tres provincias, ya proyectada (`region-map.json`). */
export const RegionMapSchema = z.object({
  source: z.object({ publisher: z.string(), license: z.string(), url: z.string() }).passthrough(),
  viewBox: z.string(), region: z.string(),
  provinces: z.array(z.object({
    name: z.string(), path: z.string(),
    city: z.object({ name: z.string(), x: z.number(), y: z.number() }),
  })),
});
export type RegionMap = z.infer<typeof RegionMapSchema>;

export const BuildInfoSchema = z.object({
  dataVersion: z.string(),
  chapters: z.array(z.object({ id: z.string(), leafStart: z.number(), leafEnd: z.number() })),
  knownIssues: z.array(z.object({ id: z.string(), type: z.string(), detail: z.string(), status: z.string() }).passthrough()),
});
export type BuildInfo = z.infer<typeof BuildInfoSchema>;
